import CRMTabs from '@components/Common/CRMTabs';
import { chartColor } from '@components/Common/Charts/chartPalette';
import EmptyState from '@components/Common/EmptyState';
import FilterRow from '@components/Common/FilterRow';
import KpiTile from '@components/Common/KpiTile';
import RecordLink from '@components/Common/RecordLink';
import SignInAttempts from './SignInAttempts';
import SelectFormField from '@components/Common/SelectFormField';
import SkeletonLoader from '@components/Common/Skeleton/SkeletonLoader';
import MintlyTable from '@components/Common/Table';
import {
  ACTOR_TYPES,
  actionLabel,
  actorPath,
  actorTypeLabel,
  entityPath,
} from '@constants/activityLabels';
import {
  getActivityActionsQuery,
  getActivityQuery,
  getActivitySummaryQuery,
} from '@constants/graphQLQueries';
import dayjs from '@helpers/date';
import { fetchGraphQLApiData } from '@helpers/graphQLApi';
import BadgeOutlinedIcon from '@mui/icons-material/BadgeOutlined';
import BoltOutlinedIcon from '@mui/icons-material/BoltOutlined';
import BusinessCenterOutlinedIcon from '@mui/icons-material/BusinessCenterOutlined';
import PeopleAltOutlinedIcon from '@mui/icons-material/PeopleAltOutlined';
import { Box, Card, CardContent, Chip, Typography } from '@mui/material';
import { LineChart } from '@mui/x-charts/LineChart';
import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { COUNT_Y_AXIS, linearSeries } from '@components/Common/Charts/chartAxes';
import { formatDateTime } from '@helpers/date';

interface ActivityRow {
  id: string;
  actorId: string | null;
  actorType: string | null;
  actorRole: string | null;
  actorName: string | null;
  actorEmail: string | null;
  action: string;
  entityType: string | null;
  entityId: string | null;
  timestamp: string | null;
}

interface ActivityDay {
  day: string;
  jobSeekers: number;
  employers: number;
  staff: number;
  events: number;
}

const PAGE_SIZE = 50;
const DAY_RANGES = [7, 30, 90];
const COLUMNS = ['when', 'who', 'type', 'action', 'record'];

const num = (n: number) => n.toLocaleString('en-IN');

/**
 * The Activity monitor: who did what, and how many people were active each day.
 *
 * Every successful action on the site and in the Control Center is recorded by the backend,
 * including sign-ups, sign-ins and purchases, and kept for a year. A person's own history is this
 * view with `?actor=<id>` -- the workspaces link here -- so there is one screen to learn, not one
 * per record type.
 */
const VIEWS = [
  { key: '', label: 'Everything' },
  { key: 'sign-ins', label: 'Sign-in attempts' },
];

/**
 * Two views on one tab: everything people did, and the Control Center's sign-in attempts. The view
 * lives in `?view=` so the "Repeated failed sign-ins" alert can link straight to it.
 */
const ActivityMonitor: React.FC = () => {
  const [params, setParams] = useSearchParams();
  const view = params.get('view') === 'sign-ins' ? 'sign-ins' : '';
  const setView = (key: string) => {
    // Each view's filters mean nothing to the other, so switching starts clean.
    const next = new URLSearchParams();
    if (key) next.set('view', key);
    setParams(next, { replace: true });
  };

  return (
    <>
      <CRMTabs
        variant="inline"
        tabs={VIEWS.map((v) => ({ label: v.label }))}
        value={Math.max(0, VIEWS.findIndex((v) => v.key === view))}
        onChange={(i) => setView(VIEWS[i].key)}
      />
      {view === 'sign-ins' ? <SignInAttempts /> : <ActivityFeed />}
    </>
  );
};

const ActivityFeed: React.FC = () => {
  const [params, setParams] = useSearchParams();
  const actorId = params.get('actor') ?? '';
  const actorType = params.get('who') ?? '';
  const action = params.get('action') ?? '';

  const [days, setDays] = useState(30);
  const [summary, setSummary] = useState<ActivityDay[] | null>(null);
  const [summaryError, setSummaryError] = useState(false);
  const [actions, setActions] = useState<string[]>([]);
  const [rows, setRows] = useState<ActivityRow[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const setParam = (key: string, value: string) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value);
    else next.delete(key);
    setParams(next, { replace: true });
    setPage(0);
  };

  useEffect(() => {
    fetchGraphQLApiData(getActivityActionsQuery())
      .then((r) => setActions(r?.data?.getActivityActions ?? []))
      .catch(() => setActions([]));
  }, []);

  useEffect(() => {
    let live = true;
    setSummaryError(false);
    fetchGraphQLApiData(getActivitySummaryQuery(days))
      .then((r) => { if (live) setSummary(r?.data?.getActivitySummary ?? []); })
      // Not [] -- a failed fetch must not read as "nobody did anything".
      .catch(() => { if (live) { setSummary(null); setSummaryError(true); } });
    return () => { live = false; };
  }, [days]);

  useEffect(() => {
    let live = true;
    setLoading(true);
    setError(false);
    fetchGraphQLApiData(getActivityQuery({ actorId, actorType, action }, page, PAGE_SIZE))
      .then((r) => {
        if (!live) return;
        setRows(r?.data?.getActivity?.items ?? []);
        setTotal(r?.data?.getActivity?.total ?? 0);
      })
      .catch(() => { if (live) setError(true); })
      .finally(() => { if (live) setLoading(false); });
    return () => { live = false; };
  }, [actorId, actorType, action, page]);

  const today = summary && summary.length > 0 ? summary[summary.length - 1] : null;
  // Unknown is a dash, not a zero: a zero is a claim about the day.
  const todayCount = (n: number | undefined) => (summaryError || !today ? '-' : num(n ?? 0));
  // The person being followed, named from their own rows once they load.
  const person = actorId ? rows.find((r) => r.actorId === actorId) : undefined;
  const personLabel = person?.actorName || person?.actorEmail || actorId;

  const actionOptions = [
    { value: '', label: 'Every action' },
    ...actions.map((a) => ({ value: a, label: actionLabel(a) })).sort((a, b) => a.label.localeCompare(b.label)),
  ];

  return (
    <>
      <Box className="cc-kpi-grid">
        <KpiTile label="Job seekers active today" value={todayCount(today?.jobSeekers)} icon={<PeopleAltOutlinedIcon />} tone="primary" />
        <KpiTile label="Employers active today" value={todayCount(today?.employers)} icon={<BusinessCenterOutlinedIcon />} tone="teal" />
        <KpiTile label="Staff active today" value={todayCount(today?.staff)} icon={<BadgeOutlinedIcon />} tone="indigo" />
        <KpiTile label="Actions today" value={todayCount(today?.events)} icon={<BoltOutlinedIcon />} tone="amber" />
      </Box>

      <Card variant="outlined" className="cc-panel cc-panel--panel an-panel">
        <CardContent>
          <Box className="an-submissions-head">
            <Typography variant="subtitle1" className="an-chart-title">People active each day</Typography>
            <CRMTabs
              variant="inline"
              tabs={DAY_RANGES.map((d) => ({ label: `${d} days` }))}
              value={DAY_RANGES.indexOf(days)}
              onChange={(i) => setDays(DAY_RANGES[i])}
            />
          </Box>
          {summaryError ? (
            <EmptyState compact title="Activity could not be loaded" body="Try again in a moment." />
          ) : summary === null ? (
            <SkeletonLoader />
          ) : summary.some((d) => d.events > 0) ? (
            <LineChart
              xAxis={[{ data: summary.map((d) => dayjs(d.day).format('D MMM')), scaleType: 'band', tickLabelStyle: { fontSize: 11 } }]}
              series={linearSeries([
                { data: summary.map((d) => d.jobSeekers), label: 'Job seekers', color: chartColor('--acc') },
                { data: summary.map((d) => d.employers), label: 'Employers', color: chartColor('--teal') },
                { data: summary.map((d) => d.staff), label: 'Staff', color: chartColor('--indigo') },
              ])}
              yAxis={COUNT_Y_AXIS}
              height={280}
              margin={{ top: 20, right: 16, bottom: 48, left: 44 }}
            />
          ) : (
            <EmptyState compact title="No activity recorded in this period" />
          )}
        </CardContent>
      </Card>

      <Card variant="outlined" className="cc-panel cc-panel--panel an-panel">
        <CardContent>
          <Typography variant="subtitle1" className="an-chart-title">What happened</Typography>
          {/* The same row every other dashboard tab filters with. */}
          <FilterRow>
            <SelectFormField
              required={false}
              name="activityWho"
              label="Who"
              value={actorType}
              options={ACTOR_TYPES}
              onChange={(_n, v) => setParam('who', String(v))}
            />
            <SelectFormField
              required={false}
              name="activityAction"
              label="Action"
              value={action}
              options={actionOptions}
              onChange={(_n, v) => setParam('action', String(v))}
            />
            {actorId && (
              <Chip label={`Only ${personLabel}`} onDelete={() => setParam('actor', '')} />
            )}
          </FilterRow>

          {loading && rows.length === 0 ? (
            <SkeletonLoader rows={10} columns={COLUMNS.length} />
          ) : error ? (
            <EmptyState compact title="Activity could not be loaded" body="Try again in a moment." />
          ) : rows.length === 0 ? (
            <EmptyState compact title="Nothing recorded for this filter" />
          ) : (
            <MintlyTable
              pagination={{
                enabled: true,
                page,
                rowsPerPage: PAGE_SIZE,
                totalNumberOfRows: total,
                handleChangePage: (_e, next) => setPage(next),
              }}
              rows={rows}
              selectors={COLUMNS}
              tableHeadRow={{
                when: <>When</>,
                who: <>Who</>,
                type: <>Type</>,
                action: <>Action</>,
                record: <>Record</>,
              }}
              tableBodyRow={{
                when: (r: ActivityRow) => <>{formatDateTime(r.timestamp, '')}</>,
                who: (r: ActivityRow) => {
                  const name = r.actorName || r.actorEmail || r.actorId || 'Unknown';
                  const path = actorPath(r.actorType, r.actorId);
                  return path ? <RecordLink to={path}>{name}</RecordLink> : <>{name}</>;
                },
                type: (r: ActivityRow) => (
                  <>{actorTypeLabel(r.actorType, r.actorRole)}</>
                ),
                action: (r: ActivityRow) => <>{actionLabel(r.action)}</>,
                record: (r: ActivityRow) => {
                  const path = entityPath(r.entityType, r.entityId);
                  if (!path) return <>{r.entityType ?? ''}</>;
                  return <RecordLink to={path}>{`Open ${r.entityType === 'JobSeeker' ? 'candidate' : r.entityType?.toLowerCase()}`}</RecordLink>;
                },
              }}
            />
          )}
        </CardContent>
      </Card>
    </>
  );
};

export default ActivityMonitor;
